<script lang="ts">
	import { onDestroy, onMount, tick } from "svelte";
	import videojs from "video.js";
	import type Player from "video.js/dist/types/player";
	import "video.js/dist/video-js.min.css";

	/**
	 * The Video.js player instance.
	 * @type {Player | null}
	 */
	let player: Player | null = null;

	/**
	 * The source URL of the video.
	 * @type {string}
	 */
	export let videoSrc;

	/**
	 * Whether the component has been mounted.
	 * @type {boolean}
	 * @default false
	 */
	let mounted = false;

	/**
	 * Reference to the video element.
	 * @type {HTMLVideoElement | null}
	 */
	let playerRef: HTMLVideoElement | null = null;

	/**
	 * Reference to the player container element.
	 * @type {HTMLDivElement | null}
	 */
	let playerContainerRef: HTMLDivElement | null = null;

	/**
	 * Handles the video metadata loaded event.
	 * Uses requestAnimationFrame to avoid forced reflow.
	 */
	function handleMetadataLoaded() {
		if (!player) return;

		// Use requestAnimationFrame to batch DOM reads and writes
		requestAnimationFrame(() => {
			// Read phase - gather all measurements
			// Check player again in case it was disposed between frames
			if (!player) return;

			const videoAspectRatio = player.videoWidth() / player.videoHeight();
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			// Calculate dimensions
			let maxWidth, maxHeight;

			if (videoAspectRatio > 1) {
				// Landscape video
				maxWidth = Math.min(viewportWidth, 800) - 48;
				maxHeight = maxWidth / videoAspectRatio;
			} else {
				// Portrait video
				maxHeight = Math.min(viewportHeight, 400);
				maxWidth = Math.min(maxHeight * videoAspectRatio, viewportWidth - 48);
			}

			// Write phase - in next frame to avoid forced reflow
			requestAnimationFrame(() => {
				// Check if component is still mounted
				if (!mounted) return;

				if (playerContainerRef) {
					playerContainerRef.style.width = `${maxWidth}px`;
					playerContainerRef.style.height = `${maxHeight}px`;
				}
			});
		});
	}

	/**
	 * On component mount, initialize the Video.js player and set up event listeners.
	 */
	onMount(() => {
		try {
			// Make sure playerRef exists before initializing
			if (!playerRef) {
				console.error("Player reference is null");
				return;
			}

			// Initialize video.js player
			player = videojs(playerRef, { fluid: true });

			// Add event listener for metadata loading
			player.on("loadedmetadata", handleMetadataLoaded);

			// Add additional cleanup for page navigation
			// This helps prevent memory leaks when navigating between pages
			const cleanupOnNavigation = () => {
				if (player) {
					try {
						player.pause();
						player.off("loadedmetadata", handleMetadataLoaded);
						player.dispose();
						player = null;
					} catch (e) {
						console.error("Error cleaning up player during navigation:", e);
					}
				}
			};

			// Listen for Astro's page navigation events
			document.addEventListener("astro:before-swap", cleanupOnNavigation);

			// Mark the component as mounted after a tick to avoid layout thrashing
			tick().then(() => {
				mounted = true;
			});

			// Return cleanup function to remove navigation event listener
			return () => {
				document.removeEventListener("astro:before-swap", cleanupOnNavigation);
			};
		} catch (error) {
			console.error("Error initializing video player:", error);
		}
	});

	/**
	 * On component destroy, clean up resources and event listeners.
	 */
	onDestroy(() => {
		try {
			if (player) {
				// Remove all event listeners
				player.off("loadedmetadata", handleMetadataLoaded);

				// Ensure player is paused before disposal
				try {
					player.pause();
				} catch (e) {
					// Ignore errors if player is already paused
				}

				// Dispose the player
				player.dispose();
				player = null;
			}

			// Clear any references to DOM elements
			playerRef = null;
			playerContainerRef = null;

			// Reset state variables
			mounted = false;
		} catch (error) {
			console.error("Error disposing video player:", error);
		}
	});
</script>

<div class="relative flex w-full justify-center">
	<!-- Video player container - use CSS classes for initial state -->
	<div
		class="w-full {mounted ? 'visible relative' : 'invisible absolute'}"
		bind:this={playerContainerRef}
	>
		<video
			bind:this={playerRef}
			class="video-js vjs-default-skin ring-company-primary rounded-lg ring-8"
			preload="auto"
			src={videoSrc}
			controls
		>
			<track kind="captions" />
			<source src={videoSrc + "#t=0.1"} type="video/mp4" />
		</video>
	</div>

	<!-- Skeleton loading placeholder - only show when not mounted -->
	{#if !mounted}
		<div
			class="h-48 w-full animate-pulse rounded-lg bg-gray-300 sm:h-64 sm:w-2/3 lg:h-96 lg:w-2/3"
		></div>
	{/if}
</div>
